import React from 'react';
import classnames from 'classnames';
import Button from 'Shared/Button';
import Spinner from 'Shared/Spinner';
import * as styles from './feedback.scss';
import shallowEquals from 'Shared/shallow-equals';
export const MINIMUIM_PENDING = 600;
export const MAXIMUM_FULFILLED = 2000;
export const MAXIMUM_REJECTED = 2000;
const defaultState = {
    status: 'default',
    text: null,
};
export default class FeedbackButton extends React.Component {
    constructor(props) {
        super(props);
        this.timerResolveVisuals = null;
        this.timerStatusDefault = null;
        this.timerDeactivate = null;
        this.isUnmounting = false;
        this.state = defaultState;
    }
    componentWillUnmount() {
        this.isUnmounting = true;
        this.clearFeedbackTimeouts();
    }
    shouldComponentUpdate(nextProps, nextState) {
        const shouldUpdate = !shallowEquals(this.props, nextProps, true) || !shallowEquals(this.state, nextState, true);
        if (!shouldUpdate) {
            console.info('Ignoring update to FeedbackButton');
        }
        return shouldUpdate;
    }
    clearFeedbackTimeouts() {
        if (this.timerResolveVisuals) {
            clearTimeout(this.timerResolveVisuals);
            this.timerResolveVisuals = null;
        }
        if (this.timerStatusDefault) {
            clearTimeout(this.timerStatusDefault);
            this.timerStatusDefault = null;
        }
        if (this.timerDeactivate) {
            clearTimeout(this.timerDeactivate);
            this.timerDeactivate = null;
        }
    }
    isPending() {
        return this.state.status === 'pending';
    }
    isRejected() {
        return this.state.status === 'rejected';
    }
    isFulfilled() {
        return this.state.status === 'fulfilled';
    }
    isActive() {
        return this.state.status !== 'default';
    }
    activate(asyncOperation, options = {}) {
        if (this.isPending()) {
            return Promise.reject(new Error("Not allowed to activate user feedback on buttons when it's pending"));
        }
        const { minimumPending = MINIMUIM_PENDING, maximumFulfilled = MAXIMUM_FULFILLED, maximumRejected = MAXIMUM_REJECTED, } = options;
        this.clearFeedbackTimeouts();
        // Extend the button props and wait for the next animation frame
        // to enable visual user feedback. If all props are passed to the
        // button per default, there's some significant performance issues
        // when there's a lot of them in the DOM.
        const activateVisuals = new Promise(resolve => {
            this.setState({
                status: 'pending',
            }, () => requestAnimationFrame(() => {
                // Sometimes you want the spinner to be visible so the user has a chance
                // to notice that something happend. Studies shows that users today expects
                // that things like this take some time. So if it's to quick they
                // assume that something went wrong. I know... stupid... right?
                this.timerResolveVisuals = setTimeout(resolve, minimumPending);
            }));
        });
        return Promise.all([asyncOperation, activateVisuals])
            .then(([text]) => this.textStatus('fulfilled', (text || ''), maximumFulfilled))
            .catch(text => {
            this.textStatus('rejected', (text || ''), maximumRejected);
            return Promise.reject(text);
        });
    }
    textStatus(status, text = '', maximumDisplay = Infinity) {
        this.clearFeedbackTimeouts();
        if (typeof text === 'string' && maximumDisplay > 0) {
            if (!this.isUnmounting) {
                this.setState({
                    text,
                }, () => requestAnimationFrame(() => {
                    // Set status to "fulfilled" or "rejected" when status text is in place
                    if (!this.isUnmounting) {
                        this.setState({
                            status,
                        }, maximumDisplay !== Infinity &&
                            (() => (this.timerStatusDefault = setTimeout(() => this.deactivate(), maximumDisplay))));
                    }
                }));
            }
        }
        else {
            this.deactivate();
        }
    }
    deactivate() {
        this.setState({
            status: 'default',
        }, () => {
            this.timerDeactivate = setTimeout(() => {
                // Using `setTimeout(...)` instead of `addEventListener('transitionend', ...)`
                // since you bearly notice the animation and wouldn't kill you if the
                // the animation for some reason stopped in the middle of it (which it shouldn't)
                this.setState(defaultState);
            }, 1000);
        });
    }
    render() {
        const buttonProps = Object.assign({}, this.props, {
            className: classnames(this.props.className, styles.base, this.isActive() && [
                styles.active,
                {
                    [styles.failed]: this.isRejected(),
                    [styles.succeeded]: this.isFulfilled(),
                    [styles.spinning]: this.isPending(),
                },
            ]),
            disabled: this.props.disabled,
            'data-feedback-text': this.state.text,
        });
        delete buttonProps.onClickWithFeedback;
        if (this.props.onClickWithFeedback) {
            buttonProps.onClick = (e) => this.isPending() || this.isFulfilled()
                ? false
                : this.props.onClickWithFeedback(e, (...args) => this.activate.apply(this, args));
        }
        return (React.createElement(Button, Object.assign({}, buttonProps),
            React.createElement("span", { className: styles.text }, this.props.children),
            this.isPending() && React.createElement(Spinner, { className: styles.spinner })));
    }
}
